import React, { useMemo } from 'react';
import { AlertCircle, CheckCircle, Package, MapPin, TrendingUp, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useInventory } from '../context/InventoryContext';
import { Card, CardContent } from '../components/ui/Card';
import Badge from '../components/ui/Badge';
import { Product } from '../types';

interface MismatchItem {
  product: Product;
  inventoryStock: number;
  locationStock: number;
  difference: number;
  locations: Array<{ name: string; quantity: number }>;
}

const MismatchInventoryPage: React.FC = () => {
  const navigate = useNavigate();
  const { products, hasStockMismatch, getProductTotalStock, getProductLocations, getCategoryName, getCategoryColor } = useInventory();

  const mismatchItems = useMemo(() => {
    const mismatches: MismatchItem[] = [];

    products.forEach(product => {
      if (hasStockMismatch(product)) {
        const locationStock = getProductTotalStock(product.id);
        const inventoryStock = product.stock_quantity;
        const locations = getProductLocations(product.id);

        mismatches.push({
          product,
          inventoryStock,
          locationStock,
          difference: Math.abs(locationStock - inventoryStock),
          locations: locations.map(loc => ({
            name: loc.location?.name || 'Unknown',
            quantity: loc.quantity
          }))
        });
      }
    });

    return mismatches.sort((a, b) => b.difference - a.difference);
  }, [products, hasStockMismatch, getProductTotalStock, getProductLocations]);

  const handleCardClick = (productId: string) => {
    navigate(`/supplies/${productId}`);
  };

  const handleBulkFix_DELETE = async () => {
    setIsFixing(true);
    setShowConfirmDialog(false);

    let successCount = 0;
    let failedCount = 0;
    const failedItems: string[] = [];

    try {
      console.log(`🔧 Starting bulk fix for ${mismatchItems.length} items...`);

      for (const item of mismatchItems) {
        try {
          console.log(`Fixing ${item.product.name} (SKU: ${item.product.sku}): ${item.inventoryStock} → ${item.locationStock}`);

          const { data, error } = await supabase
            .from('supplies')
            .update({ stock_quantity: item.locationStock })
            .eq('id', item.product.id)
            .select();

          if (error) {
            console.error(`❌ Failed to fix ${item.product.name}:`, error);
            failedItems.push(`${item.product.name} (${error.message})`);
            failedCount++;
          } else if (!data || data.length === 0) {
            console.error(`❌ No rows updated for ${item.product.name}`);
            failedItems.push(`${item.product.name} (No rows updated)`);
            failedCount++;
          } else {
            console.log(`✅ Successfully fixed ${item.product.name}`);
            successCount++;
          }
        } catch (err) {
          console.error(`❌ Exception fixing ${item.product.name}:`, err);
          failedItems.push(`${item.product.name} (${err instanceof Error ? err.message : 'Unknown error'})`);
          failedCount++;
        }
      }

      console.log(`\n📊 Fix Results: ${successCount} succeeded, ${failedCount} failed`);
      if (failedItems.length > 0) {
        console.log('Failed items:', failedItems);
      }

      setFixResults({ success: successCount, failed: failedCount });
      setShowResults(true);

      if (successCount > 0) {
        setTimeout(() => {
          window.location.reload();
        }, 3000);
      }
    } catch (error) {
      console.error('❌ Critical error during bulk fix:', error);
      setFixResults({ success: successCount, failed: failedCount });
      setShowResults(true);
    } finally {
      setIsFixing(false);
    }
  };

  const handleFixSingleItem = async (item: MismatchItem) => {
    setFixingItemId(item.product.id);

    try {
      console.log(`🔧 Fixing ${item.product.name} (SKU: ${item.product.sku}): ${item.inventoryStock} → ${item.locationStock}`);

      const { data, error } = await supabase
        .from('supplies')
        .update({ stock_quantity: item.locationStock })
        .eq('id', item.product.id)
        .select();

      if (error) {
        console.error(`❌ Failed to fix ${item.product.name}:`, error);
        alert(`Failed to fix ${item.product.name}: ${error.message}`);
      } else if (!data || data.length === 0) {
        console.error(`❌ No rows updated for ${item.product.name}`);
        alert(`Failed to fix ${item.product.name}: No rows updated`);
      } else {
        console.log(`✅ Successfully fixed ${item.product.name}`);

        setTimeout(() => {
          window.location.reload();
        }, 1000);
      }
    } catch (err) {
      console.error(`❌ Exception fixing ${item.product.name}:`, err);
      alert(`Error fixing ${item.product.name}: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setFixingItemId(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="overflow-hidden rounded-lg">
        <div className="relative">
          <img
            src="https://images.pexels.com/photos/4483610/pexels-photo-4483610.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1"
            alt="Inventory warehouse with organized storage and management systems"
            className="w-full h-64 object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-red-900/70 to-orange-900/50 flex items-center">
            <div className="px-8 text-white">
              <div className="flex items-center mb-2">
                <AlertCircle className="h-10 w-10 mr-3" />
                <h1 className="text-3xl font-bold">Mismatch Inventory</h1>
              </div>
              <p className="text-lg opacity-90">
                Resolve inventory discrepancies between location stock and main inventory
              </p>
              <div className="mt-4 flex items-center space-x-6 text-sm">
                <div className="flex items-center">
                  <Package className="h-4 w-4 mr-2" />
                  <span>{mismatchItems.length} Items with Mismatches</span>
                </div>
                <div className="flex items-center">
                  <TrendingUp className="h-4 w-4 mr-2" />
                  <span>{mismatchItems.reduce((sum, item) => sum + item.difference, 0)} Total Difference</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {mismatchItems.length > 0 && (
        <>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start">
              <AlertCircle className="h-5 w-5 text-blue-600 mr-3 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-blue-900 mb-1">
                  Understanding Stock Mismatches
                </h3>
                <p className="text-sm text-blue-800">
                  A mismatch occurs when the <strong>Main Stock Record</strong> (the system's total quantity) doesn't match the
                  <strong> Locations Total</strong> (the sum of quantities across all storage locations). This typically happens due to:
                </p>
                <ul className="list-disc list-inside mt-2 text-sm text-blue-800 space-y-1">
                  <li>Location transfers not updating main stock</li>
                  <li>Manual adjustments in one place but not the other</li>
                  <li>Data import inconsistencies</li>
                  <li>Failed transaction updates</li>
                </ul>
                <p className="text-sm text-blue-800 mt-2">
                  The fix operation will update the Main Stock Record to match the Locations Total, ensuring data consistency.
                </p>
              </div>
            </div>
          </div>

          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              Found {mismatchItems.length} {mismatchItems.length === 1 ? 'item' : 'items'} with stock mismatches
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Click on any item below to view details and resolve the mismatch
            </p>
          </div>
        </>
      )}

      {mismatchItems.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No Mismatches Found</h3>
            <p className="text-gray-600">
              All inventory items are in sync with their location quantities. Great job!
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {mismatchItems.map((item) => {
            const categoryColor = getCategoryColor(item.product.category_id);
            const categoryName = getCategoryName(item.product.category_id);

            return (
              <Card
                key={item.product.id}
                className="hover:shadow-lg hover:scale-[1.01] transition-all duration-200 cursor-pointer group"
                onClick={() => handleCardClick(item.product.id)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleCardClick(item.product.id);
                  }
                }}
                aria-label={`View details for ${item.product.name} - Stock mismatch: ${item.difference} units`}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900">{item.product.name}</h3>
                        <Badge color={categoryColor}>{categoryName}</Badge>
                        <Badge variant="secondary">SKU: {item.product.sku}</Badge>
                      </div>

                      {item.product.description && (
                        <p className="text-sm text-gray-600 mb-4">{item.product.description}</p>
                      )}

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                          <div className="flex items-center justify-between">
                            <div>
                              <span className="text-sm font-medium text-red-700">Main Stock Record</span>
                              <p className="text-xs text-red-600 mt-0.5">System quantity</p>
                            </div>
                            <AlertCircle className="h-4 w-4 text-red-600" />
                          </div>
                          <p className="text-2xl font-bold text-red-900 mt-2">
                            {item.inventoryStock}
                          </p>
                        </div>

                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                          <div className="flex items-center justify-between">
                            <div>
                              <span className="text-sm font-medium text-green-700">Locations Total</span>
                              <p className="text-xs text-green-600 mt-0.5">Sum of all locations</p>
                            </div>
                            <MapPin className="h-4 w-4 text-green-600" />
                          </div>
                          <p className="text-2xl font-bold text-green-900 mt-2">
                            {item.locationStock}
                          </p>
                        </div>

                        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-orange-700">Difference</span>
                            <TrendingUp className="h-4 w-4 text-orange-600" />
                          </div>
                          <p className="text-2xl font-bold text-orange-900 mt-2">
                            {item.difference}
                          </p>
                        </div>
                      </div>

                      <div>
                        <h4 className="text-sm font-semibold text-gray-700 mb-2">Location Breakdown:</h4>
                        <div className="flex flex-wrap gap-2">
                          {item.locations.map((loc, idx) => (
                            <div
                              key={idx}
                              className="bg-gray-100 border border-gray-300 rounded px-3 py-1 text-sm"
                            >
                              <span className="font-medium text-gray-700">{loc.name}:</span>{' '}
                              <span className="text-gray-900">{loc.quantity}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <div className="flex items-center justify-between text-sm text-blue-600 group-hover:text-blue-700 font-medium">
                          <span>Click to view details and fix this mismatch</span>
                          <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MismatchInventoryPage;
